import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import apiClient from '../../utils/api';

const ROLES = ['COMPANY_ADMIN', 'DISPATCH_AGENT', 'RECOVERY_OFFICER', 'COMPANY_AGENT', 'WAREHOUSE_MANAGER', 'INVENTORY_AUDITOR'];
const EMPTY = { firstName: '', lastName: '', email: '', password: '', phone: '', role: 'DISPATCH_AGENT', branchId: '', status: 'ACTIVE' };

export const CompanyAdminUserForm: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [form, setForm] = useState({ ...EMPTY });
  const [branches, setBranches] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user?.companyId) return;
    apiClient.listBranches(user.companyId, 100, 0).then(r => {
      const d = r.data.data;
      setBranches(d?.branches ?? d?.rows ?? (Array.isArray(d) ? d : []));
    }).catch(() => {});
    if (isEdit) {
      apiClient.listUsers(user.companyId, 1000, 0).then(r => {
        const d = r.data.data;
        const users = d?.users ?? d?.rows ?? (Array.isArray(d) ? d : []);
        const u = users.find((x: any) => x.id === id);
        if (u) setForm({ firstName: u.firstName, lastName: u.lastName, email: u.email, password: '', phone: u.phone ?? '', role: u.role, branchId: u.branchId ?? '', status: u.status });
      }).catch(() => {});
    }
  }, [id, isEdit, user]);

  const set = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const payload: any = { ...form, companyId: user!.companyId };
      if (!payload.password) delete payload.password;
      if (!payload.branchId) delete payload.branchId;
      if (isEdit) { await apiClient.updateUser(id!, payload); }
      else { await apiClient.createUser(payload); }
      navigate('/company-admin/users');
    } catch (err: any) {
      setError(err.response?.data?.error ?? 'Failed to save user');
    } finally { setLoading(false); }
  };

  return (
    <>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-ghost" onClick={() => navigate(-1)}><ArrowLeft size={16} /></button>
          <h1 className="page-title">{isEdit ? 'Edit User' : 'New Staff Member'}</h1>
        </div>
      </div>

      <div className="card" style={{ maxWidth: 700 }}>
        {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '10px 14px', borderRadius: 6, marginBottom: 16, fontSize: 14 }}>{error}</div>}
        <form onSubmit={submit}>
          <div className="form-grid">
            <div className="form-group"><label>First Name *</label><input required value={form.firstName} onChange={e => set('firstName', e.target.value)} /></div>
            <div className="form-group"><label>Last Name *</label><input required value={form.lastName} onChange={e => set('lastName', e.target.value)} /></div>
            <div className="form-group"><label>Email *</label><input required type="email" value={form.email} onChange={e => set('email', e.target.value)} /></div>
            <div className="form-group"><label>Phone</label><input value={form.phone} onChange={e => set('phone', e.target.value)} /></div>
            <div className="form-group"><label>{isEdit ? 'New Password (leave blank to keep)' : 'Password *'}</label><input type="password" required={!isEdit} value={form.password} onChange={e => set('password', e.target.value)} /></div>
            <div className="form-group">
              <label>Role *</label>
              <select value={form.role} onChange={e => set('role', e.target.value)}>
                {ROLES.map(r => <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            {['COMPANY_AGENT', 'WAREHOUSE_MANAGER', 'INVENTORY_AUDITOR'].includes(form.role) && (
              <div className="form-group">
                <label>Assigned Branch</label>
                <select value={form.branchId} onChange={e => set('branchId', e.target.value)}>
                  <option value="">— Select Branch —</option>
                  {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
            )}
            {isEdit && (
              <div className="form-group">
                <label>Status</label>
                <select value={form.status} onChange={e => set('status', e.target.value)}>
                  {['ACTIVE', 'INACTIVE', 'SUSPENDED'].map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
            )}
          </div>
          <div style={{ marginTop: 24, display: 'flex', gap: 12 }}>
            <button type="submit" className="btn btn-primary" disabled={loading}><Save size={15} /> {loading ? 'Saving...' : 'Save User'}</button>
            <button type="button" className="btn btn-ghost" onClick={() => navigate(-1)}>Cancel</button>
          </div>
        </form>
      </div>
    </>
  );
};
